import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { userAPI } from '../../services/apiService';
import { TrendingUp } from 'lucide-react';

const EarningsChart = () => {
  const [chartData, setChartData] = useState([]);
  const [period, setPeriod] = useState('weekly');
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadChartData();
  }, [period]);

  const loadChartData = async () => {
    try {
      setIsLoading(true);
      const data = await userAPI.getEarningsChart(period);
      setChartData(data.data || []);
      setTotalEarnings(data.totalEarnings || 0);
    } catch (error) {
      console.error('Error loading chart data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="border-3 border-purple-300 shadow-lg bg-white/90 backdrop-blur-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-purple-600" />
            Earnings Trends
          </CardTitle>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant={period === 'daily' ? 'default' : 'outline'}
              onClick={() => setPeriod('daily')}
              className={period === 'daily' ? 'bg-purple-600 hover:bg-purple-700' : ''}
            >
              Daily
            </Button>
            <Button 
              size="sm" 
              variant={period === 'weekly' ? 'default' : 'outline'}
              onClick={() => setPeriod('weekly')}
              className={period === 'weekly' ? 'bg-purple-600 hover:bg-purple-700' : ''}
            >
              Weekly
            </Button>
            <Button 
              size="sm" 
              variant={period === 'monthly' ? 'default' : 'outline'}
              onClick={() => setPeriod('monthly')}
              className={period === 'monthly' ? 'bg-purple-600 hover:bg-purple-700' : ''}
            >
              Monthly
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <p className="text-sm text-gray-600">Total Earnings ({period})</p>
              <p className="text-3xl font-bold text-purple-600">₹{totalEarnings.toFixed(2)}</p>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorEarnings" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#9333ea" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#9333ea" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="period" 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `₹${value}`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '2px solid #9333ea',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`₹${value}`, 'Earnings']}
                />
                <Area 
                  type="monotone" 
                  dataKey="earnings" 
                  stroke="#9333ea" 
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorEarnings)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default EarningsChart;